<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<!-- The real IMS chat mount (M2 — replaces the M0 spike's ChatIslandSpike.vue/stubHost.js).
     Mounted once in App.vue exactly where `<chat-window />` used to sit (FR-30: "a fixed-position
     overlay, mounted once here, floats above the router-view regardless of the current page") —
     the island itself now owns ALL of that positioning (ChatIslandShell, migrated in M1), so this
     wrapper is deliberately thin: mount()/unmount() the island (FR-08, AC-3.1), build+refresh the
     real IMS ChatHost (FR-09), and own web push (kept host-side per the reviewer's INT-M2 note —
     see src/chat/push.js's header comment for why). -->
<template>
  <div ref="root"></div>
</template>

<script>
import { buildImsChatHost, buildCurrentSlideSharePayload } from './buildImsChatHost';
import { setChatMountEl } from './chatMountRef';
import { registerPushNotifications, listenForPushNavigation, startActiveConversationSync } from '@/chat/push.js';
import { SHARE_IN_CHAT_EVENT } from '@/chat/shareBus';

export default {
  name: 'chat-island-mount',
  mounted() {
    // Shared with `ChatTopBarEntry.vue` (a separate component in the navbar) so its click handler
    // can command `toggleDock(el)` — see chatMountRef.js's header comment.
    setChatMountEl(this.$refs.root);
    // M2 fix F-M2.3 (AC-8.1): the composer's "Share current slide" button (island-side) has no
    // slide data of its own — it just signals via this window event. We build the actual
    // `SharePayload` from viewer state (same as the retired ChatWindow.vue) and re-emit it on the
    // SAME shareBus `onShareRequest` already subscribes to (F-M2.1) — one mechanism, two triggers.
    this._onShareCurrentSlideRequested = () => {
      const payload = buildCurrentSlideSharePayload(this);
      if (payload) {
        this.$eventBus.$emit(SHARE_IN_CHAT_EVENT, payload);
      }
    };
    import('@zaya-chat/ui-react').then(({ ZC_SHARE_CURRENT_SLIDE_EVENT }) => {
      if (this._destroyed) {
        return;
      }
      window.addEventListener(ZC_SHARE_CURRENT_SLIDE_EVENT, this._onShareCurrentSlideRequested);
      this._shareCurrentSlideEventName = ZC_SHARE_CURRENT_SLIDE_EVENT;
    });
    // Lazy-loaded (ADR-0002/0004, OQ-5): the React runtime + island payload stay off IMS's initial
    // bundle, loaded only once this component (mounted unconditionally, but cheaply, alongside the
    // rest of the authenticated shell) actually renders.
    import('@zaya-chat/ui-react').then(({ mount }) => {
      if (this._destroyed) {
        return;
      }
      this._mount = mount;
      // `$nextTick` before measuring `ImsNavbar`'s real height (inside `buildImsChatHost` ->
      // `measureFullscreenTopOffset`): this component's own `mounted()` firing doesn't guarantee
      // every OTHER app-shell component (the navbar included) has finished its own DOM update this
      // tick — waiting one tick is the standard, cheap way to guarantee an accurate
      // `getBoundingClientRect()` read rather than racing it.
      this.$nextTick(() => {
        if (this._destroyed) {
          return;
        }
        const host = buildImsChatHost(this);
        mount(this.$refs.root, host);
        // Web push (R6-R9): best-effort, never throws (see push.js). Uses the SAME host values the
        // island itself was just built from — no reliance on the island's internal SDK client.
        registerPushNotifications(host).catch(() => {});
        this._stopActiveConversationSync = startActiveConversationSync();
        import('@zaya-chat/ui-react').then(({ openConversation }) => {
          if (this._destroyed) {
            return;
          }
          this._stopPushNavigation = listenForPushNavigation((conversationId) => {
            openConversation(this.$refs.root, conversationId);
          });
        });
      });
    });
  },
  beforeDestroy() {
    this._destroyed = true;
    setChatMountEl(null);
    if (this._shareCurrentSlideEventName) {
      window.removeEventListener(this._shareCurrentSlideEventName, this._onShareCurrentSlideRequested);
    }
    if (this._stopActiveConversationSync) {
      this._stopActiveConversationSync();
    }
    if (this._stopPushNavigation) {
      this._stopPushNavigation();
    }
    const el = this.$refs.root;
    import('@zaya-chat/ui-react').then(({ unmount }) => {
      unmount(el);
    });
  },
  watch: {
    // AC-9.5: a live locale change updates the island without the island owning locale state —
    // `mount()` is idempotent per element (re-renders into the existing root, AC-10.1), so calling
    // it again with a freshly-built host (new `t`/`locale`) is exactly how IMS refreshes it.
    '$i18n.locale'() {
      if (this._mount && this.$refs.root) {
        this._mount(this.$refs.root, buildImsChatHost(this));
      }
    },
  },
};
</script>
